// ─── Beetsol InsightsDashboard (v2) ──────────────────────────────────────────
// New file: src/components/Insights/InsightsDashboard.jsx
//
// Route: /insights (or /insights/dashboard)
// Sidebar: Insights → Dashboard
//
// Changes from v1:
// — Cohort filter added (All Cohorts + workspace cohorts)
// — Date range: Last 7 Days / Last 30 Days / Last 90 Days / Custom
// — Card 1: source breakdown (Platform / Embed) added
// — Card 2: content-type aware display (video timestamp vs text avg seconds)
// — Auto-refresh every 5 minutes (background fetch, no page reload)
// — "Last updated X min ago" timestamp + manual refresh button
// — Empty state messages per card (positive tone)
// — ?highlight=card1/2/3 URL param → scroll + 2s yellow highlight animation
// — Filter persistence via URL params (audience, period, cohort)
//
// API: GET /api/insights?period=7d&audience=all&cohort=all
// Returns:
//   unmetDemand: { failedSearches, topGap, externalSearches, internalSearches,
//                  platformSearches, embedSearches, repeatFailures, aiInsight }
//   frictionPoints: { exitUnresolvedPct, highestDropoff, contentType,
//                     exitPoint, avgExitSeconds, externalUnresolvedPct,
//                     internalUnresolvedPct, aiInsight }
//   needsAttention: { complianceRisk: { overdue, dueSoon, enrolled, internal, external } }  (K6.1 pt 10)
//   internal: { compliance, overdue, monthlyActive }
//   external: { activation, retention }
//   popular: [{ term, count }]
//   cohorts: [{ id, name }]  ← for cohort filter options
//   lastUpdated: ISO timestamp
// ─────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ DATA WIRING IS PROVISIONAL — see docs/INSIGHTS_PROVISIONAL.md
// Design is final. Data-fetch (search "SEAM-5" below) was wired to
// insightsService from scratch because the real Insights components were not
// available. IF real wired Insights files exist in the repo, prefer THEIR
// data-fetch and apply this design to them instead. Verify response shapes.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { tokens } from '../../theme';
import { insightsService } from '../../services/api';
import { useInsightsAdminRole } from '../../hooks/useInsightsAdminRole';
import EmptyState  from '../common/EmptyState';
const T = tokens;
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const CARD_RETRY_DELAY = 3000; // a failed card request tries once more after this (K3 pt 19)

// A signal card's request (K3 pt 19): one try; a failure is an error or success false. Returns the response's data, or
// { failed: true }. The dashboard shows the other cards at once and tries a failed card once more on its own after a
// few seconds; if that fails too, the card shows its error state. Removed 23 Oct 2026 (K3 pt 19): turning a failed
// request into no data, so the card showed its empty sentence. Removed 23 Oct 2026 (K3 pt 19, PR #8 review): waiting
// for the second try inside the dashboard's load, which held every card on its skeleton.
async function loadCard(request) {
  try {
    const r = await request();
    if (r && r.data && r.data.success) return { data: r.data.data };
  } catch (e) { /* a failure */ }
  return { failed: true };
}

// The request behind each signal card, for the given filters.
const cardRequest = (key, { dateRange, audience, cohort }) => ({
  unmet: () => insightsService.getUnmetDemandAction({ dateRange, audience, cohort }),
  friction: () => insightsService.getFrictionPointsAction({ dateRange, audience, cohort }),
  needs: () => insightsService.getNeedsAttention({ audience, cohort }), // the period never applies (K6.1 pt 10)
}[key]);

// ── Shared components ─────────────────────────────────────────────────────────
const FilterPill = ({ children, active, onClick }) => (
  <Box onClick={onClick} sx={{ fontSize: T.font.size.xs, color: active ? T.color.textPrimary : T.color.textSecondary, background: active ? T.color.bgSecondary : 'transparent', border: `0.5px solid ${active ? T.color.borderSecondary : T.color.borderTertiary}`, borderRadius: T.radius.pill, padding: '4px 12px', cursor: 'pointer', fontFamily: T.font.family, transition: `all ${T.transition}`, userSelect: 'none' }}>
    {children}
  </Box>
);

const CardLabel = ({ children }) => (
  <Typography sx={{ fontSize: T.font.size.xs, color: T.color.textSecondary, letterSpacing: T.font.letterSpacing.label, textTransform: 'uppercase', marginBottom: '14px', fontFamily: T.font.family }}>
    {children}
  </Typography>
);

// A card heading with 'As of today' beside it: Needs Attention and the learner figures show the current state, each
// figure over its own fixed window, whatever the period selected (K3 pt 1, K6.1).
const CardLabelAsOfToday = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
    <CardLabel>{children}</CardLabel>
    <Typography sx={{ fontSize: T.font.size.xs, color: T.color.textSecondary, fontFamily: T.font.family }}>As of today</Typography>
  </Box>
);

const StatRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
    <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family }}>{label}</Typography>
    <Typography sx={{ fontSize: T.font.size.sm, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family }}>{value}</Typography>
  </Box>
);

const AiStrip = ({ text }) => (
  <Box sx={{ background: T.color.bgSecondary, borderRadius: T.radius.sm, padding: '11px 13px', marginTop: '14px' }}>
    <Typography sx={{ fontSize: '11px', color: T.color.textSecondary, letterSpacing: '0.03em', marginBottom: '3px', fontFamily: T.font.family }}>Insight</Typography>
    <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, lineHeight: 1.6, fontFamily: T.font.family }}>{text}</Typography>
  </Box>
);

const ActionBtn = ({ children, onClick, primary }) => (
  <Box onClick={onClick} sx={{ fontSize: T.font.size.sm, fontWeight: T.font.weight.medium, padding: '6px 14px', borderRadius: T.radius.sm, border: `0.5px solid ${primary ? T.color.textPrimary : T.color.borderSecondary}`, background: primary ? T.color.textPrimary : 'transparent', color: primary ? T.color.bgPrimary : T.color.textSecondary, cursor: 'pointer', fontFamily: T.font.family, transition: `all ${T.transition}`, '&:hover': { background: primary ? '#2A2A28' : T.color.bgSecondary } }}>
    {children}
  </Box>
);

const Divider = () => <Box sx={{ borderTop: `0.5px solid ${T.color.borderTertiary}`, margin: '14px 0' }} />;

const MetricBar = ({ label, value, subLabel, warn }) => (
  <Box sx={{ marginBottom: '14px', '&:last-child': { marginBottom: 0 } }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
      <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family }}>{label}</Typography>
      <Typography sx={{ fontSize: T.font.size.sm, fontWeight: T.font.weight.medium, color: warn ? T.color.alertDot : T.color.textPrimary, fontFamily: T.font.family }}>
        {value}%{subLabel && <Box component="span" sx={{ fontSize: '11px', color: T.color.alertDot, fontWeight: T.font.weight.regular, marginLeft: '4px' }}>{subLabel}</Box>}
      </Typography>
    </Box>
    <Box sx={{ height: '5px', background: T.color.bgSecondary, borderRadius: '3px' }}>
      <Box sx={{ height: '5px', borderRadius: '3px', background: warn ? T.color.alertDot : T.color.textPrimary, opacity: 0.75, width: `${parseInt(value)}%`, transition: 'width 0.3s ease' }} />
    </Box>
  </Box>
);
/*
// Empty state per card
const EmptyState = ({ message }) => (
  <Box sx={{ padding: '24px', textAlign: 'center' }}>
    <Typography sx={{ fontSize: T.font.size.md, color: T.color.textSecondary, fontFamily: T.font.family, lineHeight: 1.6 }}>
      {message}
    </Typography>
  </Box>
);*/

// A card waiting for its second try (K3 pt 19): the skeleton, not its empty sentence.
const CardPending = () => (
  <Box aria-busy="true" sx={{ height: '96px', background: T.color.bgSecondary, borderRadius: T.radius.md, animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
);

// Card error state (K3 pt 19, agreed 23 Oct): 'Something went wrong.' with Retry, in place of the card's empty sentence.
// Removed 23 Oct 2026 (K3 pt 19): 'Unable to load insights.', which no card showed.
const CardError = ({ onRetry }) => (
  <Box sx={{ padding: '20px', textAlign: 'center' }}>
    <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family, marginBottom: '10px' }}>
      Something went wrong.
    </Typography>
    <Box onClick={onRetry} sx={{ fontSize: T.font.size.sm, color: T.color.textPrimary, cursor: 'pointer', textDecoration: 'underline', fontFamily: T.font.family }}>
      Retry
    </Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const InsightsDashboard = () => {
  const navigate                = useNavigate();
  const roleChecked             = useInsightsAdminRole();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial filter state from URL params
  const [audience,  setAudience]  = useState(searchParams.get('audience') || 'all');
  const [period,    setPeriod]    = useState(searchParams.get('period')   || '7d');
  const [cohort,    setCohort]    = useState(searchParams.get('cohort')   || 'all');
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cohorts,   setCohorts]   = useState([]);

  const highlightParam = searchParams.get('highlight');
  const refreshTimer   = useRef(null);
  const card1Ref       = useRef(null);
  const card2Ref       = useRef(null);
  const card3Ref       = useRef(null);

  const cardRefs = { card1: card1Ref, card2: card2Ref, card3: card3Ref };

  // Persist filters to URL
  useEffect(() => {
    setSearchParams({ audience, period, cohort }, { replace: true });
  }, [audience, period, cohort]);

  // Highlight animation from email link
  useEffect(() => {
    if (highlightParam && cardRefs[highlightParam]?.current) {
      setTimeout(() => {
        cardRefs[highlightParam].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        cardRefs[highlightParam].current.style.transition = 'background 0.3s ease';
        cardRefs[highlightParam].current.style.background = '#FAEEDA';
        setTimeout(() => {
          if (cardRefs[highlightParam].current) {
            cardRefs[highlightParam].current.style.background = '';
          }
        }, 2000);
      }, 500);
    }
  }, [highlightParam, data]);

  // Each load gets a number, so a card's try for older filters is dropped, never shown (K3 pt 19). Retry on a card in its
  // error state asks again for that card only, with the filters on screen; the card shows its skeleton until the answer
  // comes, and the 5-minute refresh keeps trying every card as well. A card's own second try waits 3 seconds: a new load
  // cancels the waits the one before left, and a try still on its way for older filters neither holds back the new
  // load's own try nor touches the card. Removed 23 Oct 2026 (K3 pt 19, PR #8 review): a try for older filters that put
  // its card back on the skeleton before it was dropped, so the card stayed a skeleton until the refresh, and one
  // in-flight mark per card, which such a try kept.
  const loadSeq = useRef(0);
  const retrying = useRef({});
  const retryTimers = useRef([]);
  const retryCard = useCallback(async (key, seq = loadSeq.current, filters = { dateRange: parseInt(period, 10) || 7, audience, cohort }) => {
    if (seq !== loadSeq.current || retrying.current[key] === seq) return;
    retrying.current[key] = seq;
    setData((prev) => ({ ...(prev || {}), failed: { ...((prev && prev.failed) || {}), [key]: false }, pending: { ...((prev && prev.pending) || {}), [key]: true } }));
    const result = await loadCard(cardRequest(key, filters));
    if (retrying.current[key] === seq) delete retrying.current[key];
    if (seq !== loadSeq.current) return;
    setData((prev) => {
      const next = {
        ...(prev || {}),
        failed: { ...((prev && prev.failed) || {}), [key]: !!result.failed },
        pending: { ...((prev && prev.pending) || {}), [key]: false },
      };
      if (result.failed) return next;
      const d = result.data;
      if (key === 'unmet') next.unmetDemand = d ? { ...(d.unmet ?? d), actionSentence: d.actionSentence } : null;
      if (key === 'friction') next.frictionPoints = d ? { ...(d.frictionPoint ?? d), actionSentence: d.actionSentence } : null;
      if (key === 'needs') next.needsAttention = d;
      return next;
    });
  }, [period, audience, cohort]);
  useEffect(() => () => retryTimers.current.forEach(clearTimeout), []);

  const fetchData = useCallback(async (silent = false) => {
    if (!roleChecked) return;
    const seq = ++loadSeq.current;
    retryTimers.current.forEach(clearTimeout);
    retryTimers.current = [];
    if (!silent) setLoading(true);
    setError(false);
    // The period goes as dateRange 7, 30 or 90, the form the server reads (K3 pt 2). Removed 11 Oct 2026 (K3 pt 2):
    // sending it as '7d' / '30d'.
    const params = { dateRange: parseInt(period, 10) || 7, audience, cohort };
    try {
      // Data logic + response shapes confirmed against the real wired Insights
      // resp.data.data.{unmet|frictionPoint, actionSentence}; stats return
      // resp.data.data.{...}; popular returns resp.data.data.searches.
      const [
        unmet, friction, needs, internal, external, popular, cohortsRes,
      ] = await Promise.all([
        loadCard(cardRequest('unmet', params)),
        loadCard(cardRequest('friction', params)),
        loadCard(cardRequest('needs', params)),
        insightsService.getInternalStats().catch(() => null),
        insightsService.getExternalStats().catch(() => null),
        insightsService.getPopularSearches({ dateRange: params.dateRange }).catch(() => null), // the selected period (K6.1 pt 7)
        insightsService.getCohorts().catch(() => null),
      ]);

      if (seq !== loadSeq.current) return; // newer filters were chosen meanwhile
      const ok = (r) => r && r.data && r.data.success ? r.data.data : null;
      const unmetD    = unmet.data || null;
      const frictionD = friction.data || null;
      const internalD = ok(internal);
      const externalD = ok(external);
      const popularD  = ok(popular);
      const needsD    = needs.data || null;

      const assembled = {
        // Unmet demand: { totalFailed, topGap:{query}, breakdown:{external,internal} } + actionSentence
        unmetDemand: unmetD ? { ...(unmetD.unmet ?? unmetD), actionSentence: unmetD.actionSentence } : null,
        // Friction: { overall_rate, content_name, id, external_rate, internal_rate, *drop_off_time } + actionSentence
        frictionPoints: frictionD ? { ...(frictionD.frictionPoint ?? frictionD), actionSentence: frictionD.actionSentence } : null,
        needsAttention: needsD,
        internal: internalD ? {
          compliance: internalD.compliance || 0,
          overdue: internalD.overdue || 0,
          monthlyActive: internalD.monthlyActive || 0,
        } : null,
        external: externalD ? {
          activation: externalD.activation || 0,
          retention: externalD.retention || 0,
        } : null,
        popular: popularD?.searches ?? [],
        // A failed card waits for its second try, shown as its skeleton, while the others show at once.
        failed: { unmet: false, friction: false, needs: false },
        pending: { unmet: !!unmet.failed, friction: !!friction.failed, needs: !!needs.failed },
      };

      setData(assembled);
      [['unmet', unmet], ['friction', friction], ['needs', needs]].forEach(([key, result]) => {
        if (result.failed) retryTimers.current.push(setTimeout(() => retryCard(key, seq, params), CARD_RETRY_DELAY));
      });
      setCohorts(ok(cohortsRes) ?? []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Insights fetch error:', err);
      if (seq === loadSeq.current) setError(true);
    } finally {
      // Only the latest load ends the loading state (or sets the page's error flag), so a load for older filters can never
      // end a newer one's; a guard, since the page shows only its skeleton while it loads. Removed 23 Oct 2026 (K3 pt 19, PR
      // #8 review): every load ending it, older ones included.
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [period, audience, cohort, roleChecked, retryCard]);

  // Initial load + refetch on filter change
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  // Auto-refresh every 5 minutes (background, silent)
  useEffect(() => {
    refreshTimer.current = setInterval(() => fetchData(true), REFRESH_INTERVAL);
    return () => clearInterval(refreshTimer.current);
  }, [fetchData]);

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    const mins = Math.floor((new Date() - lastUpdated) / 60000);
    if (mins < 1) return 'Just now';
    return `${mins} min ago`;
  };

  // The filters go with every click-through, after a path's own '?' if it has one (K3 pt 3). Removed 11 Oct 2026
  // (K3 pt 3): a second '?' after a path such as content-health?section=attention, so the audience was lost there.
  const navigateWithFilters = (path) => {
    navigate(`${path}${path.includes('?') ? '&' : '?'}audience=${audience}&period=${period}&cohort=${cohort}`);
  };

  // The Unmet Demand and Friction Points cards keep only 'View details': content is added from Search Intelligence, and
  // added or edited from Content Health, the pages it opens (K3 pt 8.1, agreed 22 Oct). Removed 23 Oct 2026 (K3 pt
  // 8.1): the cards' own 'Create content' and 'Edit content' (handleCreateContent, handleEditContent).

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        {/* Skeleton loading */}
        {[1,2,3].map(i => (
          <Box key={i} sx={{ height: '200px', background: T.color.bgSecondary, borderRadius: T.radius.lg, marginBottom: '10px', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
        ))}
      </Box>
    );
  }

  const { unmetDemand, frictionPoints, needsAttention, internal, external, popular } = data || {};
  const failed = (data && data.failed) || {};
  const pending = (data && data.pending) || {};
  // Needs Attention, compliance only (K6.1 pt 10, K3 pt 9): learners overdue, and learners due in the next 7 days.
  const riskOverdue = needsAttention?.complianceRisk?.overdue ?? 0;
  const riskDueSoon = needsAttention?.complianceRisk?.dueSoon ?? 0;

  return (
    <Box sx={{ width: '100%' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: T.font.size.xl, fontWeight: T.font.weight.medium, color: T.color.textPrimary, letterSpacing: '-0.02em', fontFamily: T.font.family }}>
          Insights
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          {/* Filters row */}
          <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Audience */}
            <FilterPill active={audience === 'all'}      onClick={() => setAudience('all')}>All users</FilterPill>
            <FilterPill active={audience === 'external'} onClick={() => setAudience('external')}>External</FilterPill>
            <FilterPill active={audience === 'internal'} onClick={() => setAudience('internal')}>Internal</FilterPill>
            <Box sx={{ width: '1px', background: T.color.borderTertiary, margin: '0 2px' }} />

            {/* Date range */}
            <FilterPill active={period === '7d'}  onClick={() => setPeriod('7d')}>Last 7 days</FilterPill>
            <FilterPill active={period === '30d'} onClick={() => setPeriod('30d')}>Last 30 days</FilterPill>
            <FilterPill active={period === '90d'} onClick={() => setPeriod('90d')}>Last 90 days</FilterPill>
            <FilterPill active={period === 'custom'} onClick={() => setPeriod('custom')}>Custom</FilterPill>
            <Box sx={{ width: '1px', background: T.color.borderTertiary, margin: '0 2px' }} />

            {/* Cohort */}
            <Box
              component="select"
              value={cohort}
              onChange={e => setCohort(e.target.value)}
              sx={{ fontSize: T.font.size.xs, color: cohort !== 'all' ? T.color.textPrimary : T.color.textSecondary, background: T.color.bgSecondary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.pill, padding: '4px 10px', cursor: 'pointer', fontFamily: T.font.family, outline: 'none' }}
            >
              <option value="all">All cohorts</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Box>
          </Box>

          {/* Last updated + refresh */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '11px', color: T.color.textTertiary, fontFamily: T.font.family }}>
              Last updated: {formatLastUpdated()}
            </Typography>
            <Box
              onClick={() => fetchData(false)}
              sx={{ fontSize: '11px', color: T.color.textSecondary, cursor: 'pointer', fontFamily: T.font.family, '&:hover': { color: T.color.textPrimary } }}
            >
              Refresh
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Top 3 insight cards ─────────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '10px', width: '100%' }}>

        {/* Card 1 — Unmet Demand */}
        <Box
          ref={card1Ref}
          sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.alertBorder}`, borderRadius: T.radius.lg, padding: '22px 24px', transition: 'background 0.3s ease' }}
        >
          <CardLabel>Unmet Demand</CardLabel>
          {/* The card's empty sentence on its own (K3 pt 11). Removed 14 Oct 2026 (K3 pt 11): the title 'Failed searches'
              over a sentence that said no failed searches this week, whatever the period, with a long dash (K3 pt 17). */}
          {/* With searches but none without an answer: the same sentence as Search Intelligence (K3 pt 11.1, N5 pt 13), in
              grey, since good news isn't a warning (K2.3 pt 14.1). */}
          {pending.unmet ? (
            <CardPending />
          ) : failed.unmet ? (
            <CardError onRetry={() => retryCard('unmet')} />
          ) : !unmetDemand || unmetDemand.totalFailed === 0 ? (
            <EmptyState framed={false} title={null} tone={unmetDemand?.answeredSearches > 0 ? 'neutral' : 'warn'}
              message={unmetDemand?.answeredSearches > 0
                ? 'Every search in this period found an answer.'
                : 'No searches without an answer in this period.'} />
          ) : (
            <>
              <Typography sx={{ fontSize: '38px', fontWeight: T.font.weight.medium, color: T.color.textPrimary, lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '4px', fontFamily: T.font.family }}>
                {unmetDemand.totalFailed ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: T.font.size.md, color: T.color.textSecondary, marginBottom: '16px', fontFamily: T.font.family }}>
                failed searches
              </Typography>
              <Divider />
              {/* Real fields: totalFailed, topGap.query, breakdown.{external,internal}.
                  Backend-pending (data tracked, not yet on this endpoint): platform/embed
                  split, repeat failures, AI insight -> render as "—" until exposed. */}
              <StatRow label="Top gap"          value={unmetDemand.topGap?.query ?? unmetDemand.topGap ?? '—'} />
              <StatRow label="External"          value={unmetDemand.breakdown?.external != null ? `${unmetDemand.breakdown.external} searches` : '—'} />
              <StatRow label="Internal"          value={unmetDemand.breakdown?.internal != null ? `${unmetDemand.breakdown.internal} searches` : '—'} />
              <StatRow label="Via platform"      value={unmetDemand.platformSearches != null ? `${unmetDemand.platformSearches} searches` : '—'} />
              <StatRow label="Via embed"         value={unmetDemand.embedSearches != null ? `${unmetDemand.embedSearches} searches` : '—'} />
              <StatRow label="Repeat failures"   value={unmetDemand.repeatFailures != null ? `${unmetDemand.repeatFailures} terms` : '—'} />
              {(unmetDemand.actionSentence || unmetDemand.aiInsight) && <AiStrip text={unmetDemand.actionSentence || unmetDemand.aiInsight} />}
              <Divider />
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <ActionBtn onClick={() => navigateWithFilters('/insights/search-intelligence')}>View details</ActionBtn>
              </Box>
            </>
          )}
        </Box>

        {/* Card 2 — Friction Points */}
        <Box
          ref={card2Ref}
          sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, padding: '22px 24px', transition: 'background 0.3s ease' }}
        >
          <CardLabel>Friction Points</CardLabel>
          {/* The card's empty sentence on its own, only when the server names no item, that is when no one said No on
              any item (K3 pt 11, K6.1 pt 9). Removed 14 Oct 2026 (K3 pt 11): the title 'Significant drop-off points' over
              'No significant drop-off points this week. Content is landing well.', shown also when the item's rate
              rounded to 0, so one No in more than 2,000 sessions left the card empty while Content Health listed the item. */}
          {pending.friction ? (
            <CardPending />
          ) : failed.friction ? (
            <CardError onRetry={() => retryCard('friction')} />
          ) : !frictionPoints || !frictionPoints.id ? (
            // An all-clear sentence stays grey (K2.3 pt 14.1)
            <EmptyState framed={false} title={null} tone="neutral" message="No one was left without an answer in this period." />
          ) : (
            <>
              {/* Real fields: overall_rate, content_name, id, external_rate, internal_rate (each audience's own
                  rate, K6.1 pt 9c), avg_exit_time and avg_exit_seconds (the average time before exit, K6.1 pt 9d). */}
              <Typography sx={{ fontSize: '38px', fontWeight: T.font.weight.medium, color: T.color.textPrimary, lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '4px', fontFamily: T.font.family }}>
                {frictionPoints.overall_rate}%
              </Typography>
              <Typography sx={{ fontSize: T.font.size.md, color: T.color.textSecondary, marginBottom: '16px', fontFamily: T.font.family }}>
                {/* Removed 11 Oct 2026 (K6.1 pt 9d): 'exit unresolved at' the most common 10-second bucket, or the
                    time of the last exit. */}
                {frictionPoints.avg_exit_time
                  ? `exit unresolved after ${frictionPoints.avg_exit_time} on average`
                  : 'exit unresolved'}
              </Typography>
              <Divider />
              <StatRow label="Content"            value={frictionPoints.content_name ?? '—'} />
              <StatRow label="External unresolved" value={frictionPoints.external_rate != null ? `${frictionPoints.external_rate}%` : '—'} />
              <StatRow label="Internal unresolved" value={frictionPoints.internal_rate != null ? `${frictionPoints.internal_rate}%` : '—'} />
              <StatRow label="Avg time before exit" value={frictionPoints.avg_exit_time || 'Not recorded'} />
              {(frictionPoints.actionSentence || frictionPoints.aiInsight) && <AiStrip text={frictionPoints.actionSentence || frictionPoints.aiInsight} />}
              <Divider />
              <Box sx={{ display: 'flex', gap: '8px' }}>
                {/* 'View details', the same label as Unmet Demand's (K3 pts 8, 8.1). Removed 23 Oct 2026 (K3 pt 8.1): the
                    label 'View analysis'. */}
                <ActionBtn onClick={() => navigateWithFilters('/insights/content-health?section=attention')}>View details</ActionBtn>
              </Box>
            </>
          )}
        </Box>

        {/* Card 3 — Needs Attention */}
        <Box
          ref={card3Ref}
          sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, padding: '22px 24px', transition: 'background 0.3s ease' }}
        >
          <CardLabelAsOfToday>Needs Attention</CardLabelAsOfToday>
          {/* Compliance only, from the server's counts (K6.1 pt 10, K3 pt 9): each line only when its number is above 0, and
              'View compliance' always at the bottom with the same audience and cohort. Removed 12 Oct 2026 (K3 pt 9,
              K6.1 pt 10): 'N employees have required training due in 7 days' (internal only, any course with an end date),
              the Unresolved sessions group with its most recent query (read from a field the server never sent), and
              'All learners are on track.' before the empty sentence (K3 pt 11). Removed 14 Oct 2026 (K3 pt 11): the title
              'Attention' above the empty sentence. */}
          {pending.needs ? (
            <CardPending />
          ) : failed.needs ? (
            <CardError onRetry={() => retryCard('needs')} />
          ) : riskOverdue + riskDueSoon === 0 ? (
            // An all-clear sentence stays grey (K2.3 pt 14.1)
            <EmptyState framed={false} title={null} tone="neutral" message="Nothing needs attention right now." />
          ) : (
            <>
              <Box sx={{ borderTop: `0.5px solid ${T.color.borderTertiary}` }} />
              <Box sx={{ padding: '12px 0', borderBottom: `0.5px solid ${T.color.borderTertiary}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                  <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', background: T.color.alertDot, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: T.font.size.md, fontWeight: T.font.weight.medium, color: T.color.textPrimary, fontFamily: T.font.family }}>Compliance risk</Typography>
                </Box>
                {riskOverdue > 0 && (
                  <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, paddingLeft: '14px', marginBottom: '5px', fontFamily: T.font.family }}>
                    {riskOverdue} {riskOverdue === 1 ? 'learner' : 'learners'} overdue
                  </Typography>
                )}
                {riskDueSoon > 0 && (
                  <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, paddingLeft: '14px', marginBottom: '5px', fontFamily: T.font.family }}>
                    {riskDueSoon} due in the next 7 days
                  </Typography>
                )}
              </Box>
            </>
          )}
          <Box sx={{ marginTop: '12px' }}>
            {/* The theme's one outlined button (K2.3 pt 15). Removed 2 Nov 2026 (K2.3 pt 15): its own light grey
                border and grey text on a clickable box. */}
            <Button variant="outlined" size="small" onClick={() => navigateWithFilters('/insights/compliance')}>
              View compliance
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Performance bars ────────────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px', width: '100%' }}>
        <Box sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, padding: '22px 24px' }}>
          {/* A colon, not a long dash (K3 pt 17). Removed 14 Oct 2026 (K3 pt 17): 'Internal' and 'Team Performance' joined
              by a long dash, here and on Customer Engagement. */}
          <CardLabelAsOfToday>Internal: Team Performance</CardLabelAsOfToday>
          {/* The overdue count's link carries the filters, like every click-through to a detail page (K3 pt 3).
              Removed 11 Oct 2026 (K3 pt 3): a plain navigate to the compliance page, which dropped them. */}
          <MetricBar label="Compliance" value={internal?.compliance || 0} subLabel={internal?.overdue ? (<span onClick={() => navigateWithFilters('/insights/compliance')} style={{ cursor: 'pointer' }}>({internal.overdue} overdue)</span>) : null} warn={internal?.overdue > 0} />
          <MetricBar label="Monthly active" value={internal?.monthlyActive || 0} />
        </Box>
        <Box sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, padding: '22px 24px' }}>
          <CardLabelAsOfToday>External: Customer Engagement</CardLabelAsOfToday>
          <MetricBar label="Activation"     value={external?.activation || 0} />
          <MetricBar label="30-day retention" value={external?.retention || 0} />
        </Box>
      </Box>

      {/* ── Popular this week ────────────────────────────────────────────────── */}
      <Box sx={{ background: T.color.bgPrimary, border: `0.5px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, padding: '22px 24px', width: '100%' }}>
        <CardLabel>Popular this week</CardLabel>
        {!popular || popular.length === 0 ? (
          <EmptyState framed={false} title="Searches" message="No searches recorded this week yet." />
        ) : (
          popular.map((item, i) => (
            console.log('Rendering popular item:', item),
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 0', borderBottom: i < popular.length - 1 ? `0.5px solid ${T.color.borderTertiary}` : 'none' }}>
              <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textTertiary, width: '16px', flexShrink: 0, fontFamily: T.font.family }}>{i + 1}</Typography>
              <Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary, flex: 1, fontFamily: T.font.family }}>{item.query}</Typography>
              <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, fontFamily: T.font.family }}>{item.search_count} searches</Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};
export default InsightsDashboard;